import { useEffect, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Plus } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { money } from "@/lib/analytics";
import { DeleteButton } from "@/components/DeleteButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const EMPTY = { medication_name: "", ndc: "", cost: "", units: "", bought_from: "" };

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => { load(); }, []);
  async function load() { try { const { data } = await api.get("/orders"); setOrders(data); } catch { /* */ } }

  async function submit(e) {
    e.preventDefault(); setLoading(true);
    try {
      await api.post("/orders", { ...form, cost: Number(form.cost), units: Number(form.units) });
      toast.success("Order logged.");
      setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
    finally { setLoading(false); }
  }

  async function del(id) {
    try { await api.delete(`/orders/${id}`); toast.success("Order deleted."); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to delete."); }
  }

  return (
    <div>
      <h1 className="font-heading text-3xl font-bold tracking-tight">Order History</h1>
      <p className="mt-1 text-sm text-muted-foreground">Log medication purchases. Remove an entry with the delete action.</p>

      <form onSubmit={submit} className="mt-6 grid gap-3 rounded-xl border border-border/60 bg-card p-5 sm:grid-cols-2 lg:grid-cols-6">
        <div className="space-y-1.5 lg:col-span-2"><Label className="text-xs">Medication name</Label><Input data-testid="order-medication" value={form.medication_name} onChange={set("medication_name")} required /></div>
        <div className="space-y-1.5"><Label className="text-xs">NDC</Label><Input data-testid="order-ndc" value={form.ndc} onChange={set("ndc")} required /></div>
        <div className="space-y-1.5"><Label className="text-xs">Cost ($)</Label><Input type="number" step="0.01" data-testid="order-cost" value={form.cost} onChange={set("cost")} required /></div>
        <div className="space-y-1.5"><Label className="text-xs">Units</Label><Input type="number" data-testid="order-units" value={form.units} onChange={set("units")} required /></div>
        <div className="space-y-1.5"><Label className="text-xs">Bought from</Label><Input data-testid="order-source" value={form.bought_from} onChange={set("bought_from")} required /></div>
        <div className="lg:col-span-6"><Button type="submit" disabled={loading} data-testid="order-submit" className="rounded-full"><Plus className="mr-1 h-4 w-4" /> Log Order</Button></div>
      </form>

      <div className="mt-6 overflow-hidden rounded-xl border border-border/60 bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead><TableHead>Medication</TableHead><TableHead>NDC</TableHead>
              <TableHead className="text-right">Cost</TableHead><TableHead className="text-right">Units</TableHead><TableHead>Bought From</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground">No orders logged yet.</TableCell></TableRow>
            ) : orders.map((o) => (
              <TableRow key={o.id} data-testid={`order-row-${o.id}`}>
                <TableCell className="whitespace-nowrap text-sm">{format(new Date(o.date), "MMM d, yyyy h:mm a")}</TableCell>
                <TableCell className="font-medium">{o.medication_name}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{o.ndc}</TableCell>
                <TableCell className="text-right">{money(o.cost)}</TableCell>
                <TableCell className="text-right">{o.units}</TableCell>
                <TableCell>{o.bought_from}</TableCell>
                <TableCell className="text-right"><DeleteButton onConfirm={() => del(o.id)} label="this order" testId={`order-delete-${o.id}`} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
